import { AfterViewInit, ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { CellsConfig, DynamicTableCellType, DynamicTableData, DynamicTableRow, LazyTableDataSource } from '@rappi/ui/dynamic-table';
import { LazyScrollableDataSource } from '@rappi/common';

export type PoStatus = 'waiting-for-finance' | 'draft' | 'waiting-for-info' | 'closed' | 'approved';
export type PoSourceType = 'Trato' | 'Client' | 'Paid by Rappi';

export interface PurchaseOrder {
  id: number;
  uniqueKey: string;
  company: string;
  sourceType: PoSourceType;
  poName: string;
  currency: 'COP' | 'USD';
  poBudget: string;
  status: PoStatus;
}

const MOCK_PURCHASE_ORDERS: PurchaseOrder[] = [
  { id: 1, uniqueKey: 'P-143967', company: 'Philip Morris International', sourceType: 'Trato', poName: 'ZYN AON_Mayo', currency: 'COP', poBudget: '$934.809,00', status: 'waiting-for-finance' },
  { id: 2, uniqueKey: 'P-143968', company: 'Beiersdorf', sourceType: 'Client', poName: 'Ejecución Mayo - BDF crm', currency: 'USD', poBudget: '$819.619,00', status: 'draft' },
  { id: 3, uniqueKey: 'P-143969', company: 'Nestlé Colombia', sourceType: 'Trato', poName: 'JBP Nestlé_orion07', currency: 'COP', poBudget: '$57.000,00', status: 'waiting-for-finance' },
  { id: 4, uniqueKey: 'P-143970', company: 'Pagbank', sourceType: 'Client', poName: 'R4ALL | PAGBANK | Jellyfish', currency: 'COP', poBudget: '$5.000.000,00', status: 'waiting-for-info' },
  { id: 5, uniqueKey: 'P-143971', company: 'Corporate Varsal S.A.', sourceType: 'Client', poName: "Abr'26 - Powers X", currency: 'COP', poBudget: '$934.809,00', status: 'waiting-for-finance' },
  { id: 6, uniqueKey: 'P-143972', company: 'Rochinha Sorvetes', sourceType: 'Paid by Rappi', poName: 'Smart Ad_Rochinha', currency: 'USD', poBudget: '$39.999.35,00', status: 'closed' },
  { id: 7, uniqueKey: 'P-143973', company: 'Deuvel', sourceType: 'Paid by Rappi', poName: 'Deubel Mayo-26', currency: 'USD', poBudget: '$2.650,00', status: 'approved' },
  { id: 8, uniqueKey: 'P-143974', company: 'PepsiCo', sourceType: 'Client', poName: 'CRM_PEPSICO', currency: 'USD', poBudget: '$5.000.000,00', status: 'approved' },
  { id: 9, uniqueKey: 'P-143975', company: 'Bimbo - Barcel', sourceType: 'Paid by Rappi', poName: 'PBR Turbo Viral Deals', currency: 'USD', poBudget: '$819.619,00', status: 'approved' },
  { id: 10, uniqueKey: 'P-143976', company: 'Nutella Ferrero 2021', sourceType: 'Client', poName: 'Bachoco', currency: 'COP', poBudget: '$2.650,00', status: 'closed' }
];

export const PO_STATUS_LABELS: Record<PoStatus, string> = {
  'waiting-for-finance': 'Waiting for finance',
  'draft': 'Draft',
  'waiting-for-info': 'Waiting for info',
  'closed': 'Closed',
  'approved': 'Approved'
};

const SOURCE_TYPE_CLASS: Record<PoSourceType, string> = {
  'Trato': 'fh-source--trato',
  'Client': 'fh-source--client',
  'Paid by Rappi': 'fh-source--paid-by-rappi'
};

interface FilterOption { id: string; name: string; }

@Component({
  selector: 'app-purchase-orders',
  templateUrl: './purchase-orders.component.html',
  styleUrls: ['./purchase-orders.component.scss']
})
export class PurchaseOrdersComponent implements OnInit, AfterViewInit {
  @ViewChild('actionsTemplate') actionsTemplate: TemplateRef<any>;
  @ViewChild('statusTemplate') statusTemplate: TemplateRef<any>;
  @ViewChild('sourceTypeTemplate') sourceTypeTemplate: TemplateRef<any>;

  tableData: DynamicTableData;

  filterCompanies: string[] = [];
  filterSourceTypes: string[] = [];
  filterStatuses: string[] = [];
  searchTerm = '';

  readonly selectConfig = {
    search: true,
    includeAll: true,
    showTotalsChip: false,
    maxNumberOfChips: 1,
    panelClass: 'one-select'
  };

  readonly selectConfigNoSearch = {
    ...this.selectConfig,
    search: false
  };

  companyDataSource = new LazyScrollableDataSource(this.fetchCompanyOptions.bind(this));
  sourceTypeDataSource = new LazyScrollableDataSource(this.fetchSourceTypeOptions.bind(this));
  statusDataSource = new LazyScrollableDataSource(this.fetchStatusOptions.bind(this));

  private readonly PAGE_SIZE = 10;

  constructor(private readonly router: Router, private readonly cdr: ChangeDetectorRef) {}

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    this.tableData = this.buildTableConfig();
    this.cdr.detectChanges();
  }

  onSearch(event: Event): void {
    this.searchTerm = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.resetTable();
  }

  onCompanyChange(values: FilterOption[]): void {
    this.filterCompanies = values?.map(v => v.id) ?? [];
    this.resetTable();
  }

  onSourceTypeChange(values: FilterOption[]): void {
    this.filterSourceTypes = values?.map(v => v.id) ?? [];
    this.resetTable();
  }

  onStatusChange(values: FilterOption[]): void {
    this.filterStatuses = values?.map(v => v.id) ?? [];
    this.resetTable();
  }

  viewOrder(po: PurchaseOrder): void {
    this.router.navigate(['/financial-hub/purchase-orders', po.id]);
  }

  private resetTable(): void {
    (this.tableData.dataSource as LazyTableDataSource<DynamicTableRow>).resetTable();
  }

  private buildTableConfig(): DynamicTableData {
    const dataSource = new LazyTableDataSource<DynamicTableRow>(
      this.fetchOrders.bind(this),
      this.PAGE_SIZE,
      'uniqueKey',
      1
    );

    const columns = ['company', 'uniqueKey', 'sourceType', 'poName', 'currency', 'poBudget', 'status', 'actions'];

    const cellsConfig: CellsConfig = {
      poName: {
        type: DynamicTableCellType.text,
        title: 'PO Name'
      },
      uniqueKey: {
        type: DynamicTableCellType.text,
        title: 'PO ID' 
      },
      sourceType: {
        type: DynamicTableCellType.templateRef,
        title: 'Source type' 
      },
      company: {
        type: DynamicTableCellType.text,
        title: 'Company' 
      },
      currency: {
        type: DynamicTableCellType.text,
        title: 'Currency' 
      },
      poBudget: {
        type: DynamicTableCellType.text,
        title: 'PO Budget' 
      },
      status: {
        type: DynamicTableCellType.templateRef,
        title: 'Status' 
      },
      actions: {
        type: DynamicTableCellType.templateRef,
        title: 'Actions'
      }
    };

    return {
      dataSource,
      hideSearch: true,
      pagination: true,
      lazyLoaded: true,
      message: { title: 'No purchase orders found' },
      paginatorOpts: { pageSize: this.PAGE_SIZE, pageSizeOptions: [10, 25, 50] },
      columns,
      cellsConfig
    };
  }

  private fetchOrders(page: number, limit: number): Observable<DynamicTableRow[]> {
    const filtered = MOCK_PURCHASE_ORDERS.filter(po => {
      const matchCompany = !this.filterCompanies.length || this.filterCompanies.includes(po.company);
      const matchSource = !this.filterSourceTypes.length || this.filterSourceTypes.includes(po.sourceType);
      const matchStatus = !this.filterStatuses.length || this.filterStatuses.includes(po.status);
      const matchSearch = !this.searchTerm ||
        po.poName.toLowerCase().includes(this.searchTerm) ||
        po.uniqueKey.toLowerCase().includes(this.searchTerm) ||
        po.company.toLowerCase().includes(this.searchTerm);
      return matchCompany && matchSource && matchStatus && matchSearch;
    });

    const start = (page - 1) * limit;
    const rows: DynamicTableRow[] = filtered.slice(start, start + limit).map(po => ({
      poName: { value: po.poName },
      uniqueKey: { value: po.uniqueKey },
      sourceType: { templateRef: this.sourceTypeTemplate, context: { sourceType: po.sourceType, colorClass: SOURCE_TYPE_CLASS[po.sourceType] } },
      company: { value: po.company },
      currency: { value: po.currency },
      poBudget: { value: po.poBudget },
      status: { templateRef: this.statusTemplate, context: { status: po.status, label: PO_STATUS_LABELS[po.status] } },
      actions: { templateRef: this.actionsTemplate, context: { po } }
    }));

    return of(rows).pipe(
      delay(0),
      tap(() => {
        if (this.tableData?.paginator) {
          this.tableData.paginator.length = filtered.length;
        }
      })
    );
  }

  private fetchCompanyOptions(): Observable<FilterOption[]> {
    const companies = [...new Set(MOCK_PURCHASE_ORDERS.map(po => po.company))]
      .map(c => ({ id: c, name: c }));
    return of(companies);
  }

  private fetchSourceTypeOptions(): Observable<FilterOption[]> {
    return of([
      { id: 'Trato', name: 'Trato' },
      { id: 'Client', name: 'Client' },
      { id: 'Paid by Rappi', name: 'Paid by Rappi' }
    ]);
  }

  private fetchStatusOptions(): Observable<FilterOption[]> {
    return of(
      (Object.entries(PO_STATUS_LABELS) as [PoStatus, string][]).map(([id, name]) => ({ id, name }))
    );
  }
}
